import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Phone, MessageCircle, Download, CreditCard } from 'lucide-react';
import { jsPDF } from 'jspdf';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);
const API = process.env.REACT_APP_BACKEND_URL;
const SHOW_PAY_BUTTON = false; // parked — clients pay via GHL invoice until Stripe goes live

function PayNowCTA({ data }) {
  const [provider, setProvider] = React.useState('ghl');
  const [starting, setStarting] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API}/api/config/features`)
      .then(r => r.json())
      .then(f => setProvider(f.payments_provider))
      .catch(() => {});
  }, []);

  const useStripe = provider === 'stripe' && data.proposalId;
  if (!useStripe && !data.paymentUrl) return null;

  const label = `Pay ${data.paymentPercent && data.paymentPercent < 100 ? `${data.paymentPercent}% Deposit` : 'Now'}${data.amountDue ? ` — ${fmt(data.amountDue)}` : ''}`;

  const startStripeCheckout = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal_id: data.proposalId, origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      throw new Error(d.detail || 'Checkout failed');
    } catch (e) {
      console.error('Stripe checkout failed', e);
      if (data.paymentUrl) window.open(data.paymentUrl, '_blank');
    }
    setStarting(false);
  };

  const cls = "inline-flex items-center gap-2.5 px-10 py-5 bg-gold text-[#0a0a0a] font-bold text-base uppercase tracking-wide hover:bg-[#E5B954] active:scale-[0.98] transition-all shadow-[0_0_40px_rgba(212,168,67,0.35)]";

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="mb-6">
      {useStripe ? (
        <button onClick={startStripeCheckout} disabled={starting} className={cls} data-testid="pay-now-btn">
          <CreditCard className="w-5 h-5" /> {starting ? 'Opening checkout…' : label}
        </button>
      ) : (
        <a href={data.paymentUrl} target="_blank" rel="noopener noreferrer" className={cls} data-testid="pay-now-btn">
          <CreditCard className="w-5 h-5" /> {label}
        </a>
      )}
      <p className="text-white/30 text-xs mt-3">Secure checkout powered by Stripe — pay by card in seconds</p>
    </motion.div>
  );
}

function generateEstimatePDF(data) {
  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header bar
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, W, 40, 'F');
  doc.setFillColor(212, 168, 67);
  doc.rect(0, 40, W, 2, 'F');

  doc.setTextColor(212, 168, 67);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EXOTIC ANIMALS MIAMI', 15, 20);
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text('Private Exotic Animal Experiences', 15, 28);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text('954-299-0209  |  bookings@miamiexoticanimals.com', 15, 35);

  y = 52;

  // Estimate title
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('ESTIMATE', 15, y);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, W - 15, y, { align: 'right' });
  y += 12;

  // Client info
  doc.setFillColor(248, 248, 248);
  doc.rect(15, y - 4, W - 30, 32, 'F');
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text('PREPARED FOR', 20, y + 2);
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Client', 20, y + 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  if (data.email) doc.text(data.email, 20, y + 17);
  if (data.phone) doc.text(data.phone, 20, y + 23);

  // Event info on right
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text('EVENT DETAILS', W - 80, y + 2);
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9);
  let ey = y + 10;
  if (data.eventType) { doc.text(data.eventType, W - 80, ey); ey += 7; }
  if (data.eventDate) { doc.text(data.eventDate, W - 80, ey); ey += 7; }
  if (data.eventTime) { doc.text(data.eventTime, W - 80, ey); ey += 7; }
  if (data.guestCount) { doc.text(`${data.guestCount} guests`, W - 80, ey); }

  y += 40;

  // Line items header
  doc.setFillColor(10, 10, 10);
  doc.rect(15, y, W - 30, 8, 'F');
  doc.setTextColor(212, 168, 67);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('ITEM', 20, y + 5.5);
  doc.text('QTY', W - 75, y + 5.5, { align: 'center' });
  doc.text('HRS', W - 55, y + 5.5, { align: 'center' });
  doc.text('PRICE', W - 20, y + 5.5, { align: 'right' });
  y += 12;

  // Line items
  const items = data.items || [];
  doc.setFont('helvetica', 'normal');
  items.forEach((item, i) => {
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    const bg = i % 2 === 0 ? 255 : 248;
    doc.setFillColor(bg, bg, bg);
    doc.rect(15, y - 4, W - 30, 10, 'F');

    doc.setTextColor(40, 40, 40);
    doc.setFontSize(9);
    const name = item.quotePending ? `${item.name} (Quote Pending)` : item.name;
    doc.text(name.substring(0, 50), 20, y + 2);

    doc.setTextColor(80, 80, 80);
    doc.text(String(item.quantity || 1), W - 75, y + 2, { align: 'center' });
    doc.text(String(item.hours || 1), W - 55, y + 2, { align: 'center' });

    if (item.quotePending) {
      doc.setTextColor(180, 140, 40);
      doc.text('TBD', W - 20, y + 2, { align: 'right' });
    } else {
      doc.setTextColor(40, 40, 40);
      const lineTotal = (item.price + (item.additionalHourPrice || 0) * ((item.hours || 1) - 1)) * (item.quantity || 1);
      doc.text(fmt(lineTotal), W - 20, y + 2, { align: 'right' });
    }
    y += 10;
  });

  // Delivery fee
  if (data.deliveryFee > 0) {
    doc.setFillColor(248, 248, 248);
    doc.rect(15, y - 4, W - 30, 10, 'F');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(9);
    doc.text('Delivery Fee', 20, y + 2);
    doc.text(fmt(data.deliveryFee), W - 20, y + 2, { align: 'right' });
    y += 10;
  }

  // Total
  y += 4;
  doc.setFillColor(10, 10, 10);
  doc.rect(W - 95, y - 4, 80, 14, 'F');
  doc.setTextColor(212, 168, 67);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('ESTIMATED TOTAL', W - 90, y + 5);
  doc.setFontSize(14);
  doc.text(fmt(data.total || 0), W - 20, y + 5, { align: 'right' });

  if (data.hasQuotePending) {
    y += 18;
    doc.setTextColor(180, 140, 40);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('* Some items are pending a custom quote and are not included in the total above.', 15, y);
  }

  // Footer
  y = doc.internal.pageSize.getHeight() - 25;
  doc.setDrawColor(212, 168, 67);
  doc.line(15, y, W - 15, y);
  y += 6;
  doc.setTextColor(140, 140, 140);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('This estimate is valid for 30 days. Final pricing may vary based on availability and event requirements.', 15, y);
  y += 5;
  doc.text('Exotic Animals Miami  |  miamiexoticanimals.com  |  954-299-0209', 15, y);

  return doc;
}

export default function ThankYouPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const data = useMemo(() => location.state || {}, [location.state]);

  const handleDownload = useCallback(() => {
    const doc = generateEstimatePDF(data);
    const fileName = `EAM-Estimate-${data.firstName || 'Client'}-${(data.eventDate || 'undated').replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
  }, [data]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg w-full text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2, type: 'spring' }}
          className="w-20 h-20 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center mx-auto mb-8"
        >
          <CheckCircle className="w-10 h-10 text-gold" />
        </motion.div>

        {/* Heading */}
        <h1 className="font-display text-3xl md:text-4xl text-white uppercase tracking-wide mb-3" data-testid="thankyou-heading">
          Thank You{data.firstName ? `, ${data.firstName}` : ''}!
        </h1>
        <p className="text-white/50 text-sm md:text-base mb-8 leading-relaxed">
          {data.paymentUrl
            ? 'Your estimate has been submitted and your invoice is ready. Secure your date by paying your deposit now — or our team will follow up shortly.'
            : 'Your estimate has been submitted successfully. Our team will review your request and reach out shortly to confirm details.'}
        </p>

        {/* Pay Now CTA */}
        {SHOW_PAY_BUTTON && (data.paymentUrl || data.proposalId) && <PayNowCTA data={data} />}

        {/* Download Button */}
        {data.total && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-8"
          >
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-gold text-[#0a0a0a] font-semibold text-sm uppercase tracking-wide hover:bg-[#E5B954] active:scale-[0.98] transition-all shadow-[0_0_30px_rgba(212,168,67,0.2)]"
              data-testid="download-estimate-btn"
            >
              <Download className="w-5 h-5" />
              Download Your Estimate
            </button>
          </motion.div>
        )}

        {/* Estimate Summary */}
        {data.total && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white/5 border border-white/10 p-6 mb-8 text-left"
            data-testid="thankyou-summary"
          >
            <h3 className="text-white/40 text-xs uppercase tracking-wider mb-4">Estimate Summary</h3>
            <div className="space-y-2 text-sm">
              {data.eventType && (
                <div className="flex justify-between">
                  <span className="text-white/50">Event</span>
                  <span className="text-white">{data.eventType}</span>
                </div>
              )}
              {data.eventDate && (
                <div className="flex justify-between">
                  <span className="text-white/50">Date</span>
                  <span className="text-white">{data.eventDate}</span>
                </div>
              )}
              {data.guestCount && (
                <div className="flex justify-between">
                  <span className="text-white/50">Guests</span>
                  <span className="text-white">{data.guestCount}</span>
                </div>
              )}
              {data.items && data.items.length > 0 && (
                <div className="pt-2 mt-2 border-t border-white/5 space-y-1.5">
                  {data.items.map((item, i) => (
                    <div key={`item-${item.name}-${i}`} className="flex justify-between">
                      <span className="text-white/60 text-xs">
                        {item.name}{item.quantity > 1 ? ` x${item.quantity}` : ''}
                      </span>
                      <span className="text-white/60 text-xs">
                        {item.quotePending ? 'TBD' : fmt(item.price * (item.quantity || 1))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {data.deliveryFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Delivery</span>
                  <span className="text-white">{fmt(data.deliveryFee)}</span>
                </div>
              )}
              <div className="pt-3 mt-3 border-t border-white/10 flex justify-between">
                <span className="text-white/70 font-medium">Estimated Total</span>
                <span className="text-gold font-display text-lg">{fmt(data.total)}</span>
              </div>
              {data.hasQuotePending && (
                <p className="text-amber-400/80 text-xs">+ Items pending quote (not included)</p>
              )}
            </div>
          </motion.div>
        )}

        {/* What's Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white/5 border border-white/10 p-6 mb-8 text-left"
          data-testid="thankyou-next-steps"
        >
          <h3 className="text-white/40 text-xs uppercase tracking-wider mb-4">What Happens Next</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-gold text-xs font-medium">1</span>
              </div>
              <p className="text-white/60 text-sm">We'll review your estimate and confirm animal & prop availability</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-gold text-xs font-medium">2</span>
              </div>
              <p className="text-white/60 text-sm">You'll receive a final invoice with a secure payment link</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-gold text-xs font-medium">3</span>
              </div>
              <p className="text-white/60 text-sm">Once confirmed, we'll handle all the logistics for your event</p>
            </div>
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex gap-3 justify-center mb-8"
        >
          <a
            href="tel:+17868915777"
            className="flex items-center gap-2 px-5 py-2.5 border border-white/10 text-white/60 text-sm hover:border-gold/30 hover:text-gold transition-colors"
            data-testid="thankyou-call"
          >
            <Phone className="w-4 h-4" /> Call Us
          </a>
          <a
            href="sms:+17868915777"
            className="flex items-center gap-2 px-5 py-2.5 border border-white/10 text-white/60 text-sm hover:border-gold/30 hover:text-gold transition-colors"
            data-testid="thankyou-text"
          >
            <MessageCircle className="w-4 h-4" /> Text Us
          </a>
        </motion.div>

        {/* Back */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-white/30 hover:text-white/60 text-xs mx-auto transition-colors"
          data-testid="thankyou-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </button>
      </motion.div>
    </div>
  );
}
